import {isNil} from 'lodash';
import {Constants} from '../constants/constants';
import {AESEncryptDecryptService} from '../_services/aesencrypt-decrypt.service';
import {Injectable} from '@angular/core';
import {User} from '../_models';

@Injectable({
    providedIn: 'root'
})
export class UtilsService {
    constructor(private _AESEncryptDecryptService: AESEncryptDecryptService) {

    }

    public getCurrentUser() {
        return this.getCurrentUserFromSession();
    }

    private getCurrentUserFromSession() {
        if (localStorage.getItem(Constants.CURRENT_USER) && !isNil(this._AESEncryptDecryptService.decrypt(localStorage.getItem(Constants.CURRENT_USER)))) {
            const user = JSON.parse(this._AESEncryptDecryptService.decrypt(localStorage.getItem(Constants.CURRENT_USER)));
            if (this._AESEncryptDecryptService.decrypt(localStorage.getItem(Constants.ORG_SETTINGS))) {
                user.orgSettings = JSON.parse(this._AESEncryptDecryptService.decrypt(localStorage.getItem(Constants.ORG_SETTINGS)));
            }
            user.selectedRole = this.deriveSelectedRole(user, localStorage);
            return user;
        } else if (sessionStorage.getItem(Constants.CURRENT_USER) !== null && !isNil(sessionStorage.getItem(Constants.CURRENT_USER))) {
            const user = JSON.parse(this._AESEncryptDecryptService.decrypt(sessionStorage.getItem(Constants.CURRENT_USER)));
            if (this._AESEncryptDecryptService.decrypt(sessionStorage.getItem(Constants.ORG_SETTINGS))) {
                user.orgSettings = JSON.parse(this._AESEncryptDecryptService.decrypt(sessionStorage.getItem(Constants.ORG_SETTINGS)));
            }
            user.selectedRole = this.deriveSelectedRole(user, sessionStorage);
            return user;
        }
    }

    /**
     * Resolve the active selectedRole. Prefer the persisted SELECTED_ROLE key;
     * fall back to the first role on the user when it's missing/blank. Without
     * this fallback, refreshing /sidebar/cashier left the sidebar's gating
     * *ngIf falsy and rendered a blank shell — only /select-role re-populated
     * the storage key and made the page work again.
     */
    private deriveSelectedRole(user: any, store: Storage): string | undefined {
        const stored = store.getItem(Constants.SELECTED_ROLE);
        if (stored) {
            const decrypted = this._AESEncryptDecryptService.decrypt(stored);
            if (decrypted) return decrypted;
        }
        if (Array.isArray(user?.role) && user.role.length > 0 && user.role[0]) {
            return user.role[0];
        }
        return undefined;
    }

    public getSelectedRoleFromSession() {
        if (this._AESEncryptDecryptService.decrypt(localStorage.getItem(Constants.SELECTED_ROLE))) {
            return this._AESEncryptDecryptService.decrypt(localStorage.getItem(Constants.SELECTED_ROLE));
        } else if (!isNil(sessionStorage.getItem(Constants.SELECTED_ROLE))) {
            return this._AESEncryptDecryptService.decrypt(sessionStorage.getItem(Constants.SELECTED_ROLE));
        }
    }

    public setCurrentUser(user: User, role?) {
        if(role !== Constants.WEBSITE && role !== Constants.QR_ORDER) {
            this.setCurrentUserAndRoleToLogin(user, role);
        } else {
            return this.setCurrentUserToSession(user, role);
        }
    }

    private setCurrentUserToSession(user: User, role?) {
        // Persist orgSettings under its own key, then store the user *without
        // orgSettings* under CURRENT_USER. Build a fresh object — the previous
        // implementation mutated `user.orgSettings = null` on the caller's
        // copy, which broke any in-memory subscriber holding the same
        // reference (e.g. `authenticationService._authenticatedUser`),
        // causing UI components to read a stale/null orgSettings.
        sessionStorage.setItem(Constants.ORG_SETTINGS, this._AESEncryptDecryptService.encrypt(JSON.stringify(user?.orgSettings ?? null)));
        const {orgSettings: _omit, ...userWithoutOrg} = (user as any) || {};
        sessionStorage.setItem(Constants.CURRENT_USER, this._AESEncryptDecryptService.encrypt(JSON.stringify(userWithoutOrg)));
        if(role) {
            sessionStorage.setItem(Constants.SELECTED_ROLE, this._AESEncryptDecryptService.encrypt(role));
        }
    }


    private setCurrentUserAndRoleToLogin(user: User, role?) {
        // See setCurrentUserToSession for why the user object is no longer
        // mutated in place.
        localStorage.setItem(Constants.ORG_SETTINGS, this._AESEncryptDecryptService.encrypt(JSON.stringify(user?.orgSettings ?? null)));
        const {orgSettings: _omit, ...userWithoutOrg} = (user as any) || {};
        localStorage.setItem(Constants.CURRENT_USER, this._AESEncryptDecryptService.encrypt(JSON.stringify(userWithoutOrg)));
        if(role) {
            localStorage.setItem(Constants.SELECTED_ROLE, this._AESEncryptDecryptService.encrypt(role));
        }
    }

    public setSelectedRole(role: string) {
        this.setRoleToSession(role);
    }

    private setRoleToSession(role: string) {
        localStorage.setItem('selectedRole', this._AESEncryptDecryptService.encrypt(role));
    }

    public updateOrgSettings(orgSettings: any): void {
        const encrypted = this._AESEncryptDecryptService.encrypt(JSON.stringify(orgSettings));
        if (localStorage.getItem(Constants.CURRENT_USER)) {
            localStorage.setItem(Constants.ORG_SETTINGS, encrypted);
        } else if (sessionStorage.getItem(Constants.CURRENT_USER)) {
            sessionStorage.setItem(Constants.ORG_SETTINGS, encrypted);
        }
    }
}
